import React, { useEffect, useState } from 'react';

import { Box, Flex, HStack, SimpleGrid } from '@chakra-ui/react';
import { parseISO, set } from 'date-fns';
import { filter, isEmpty, isNil, reject, uniq } from 'ramda';
import { useHistory, useLocation } from 'react-router-dom';

import AppModal from 'components/modules/AppModal';
import Button from 'components/primitives/Button';
import DatePicker from 'components/primitives/DatePicker';
import Select from 'components/primitives/Select';
import Typography from 'components/primitives/Typography';
import locations from 'constants/locations';
import marketPrice from 'constants/marketPrice.json';
import { PropertyTypes } from 'constants/property';
import { QuickListingLogFilters } from 'types/Property';
import ucFirstMultiple from 'utils/string/ucFirstMultiple';

import { QuickListingFiltersProps } from './QuickListingFilters.props';

const regions = locations.map((loc) => ({
  label: loc.region,
  value: loc.region,
}));

const QuickListingFilters = (props: QuickListingFiltersProps): JSX.Element => {
  // Hook for routing and location management
  const history = useHistory();
  const location = useLocation();

  // State for storing filters applied by the user
  const [filters, setFilters] = useState<
    Partial<Record<keyof QuickListingLogFilters, string>>
  >({});

  // States for managing date filters
  const [matchDateFrom, setMatchDateFrom] = useState<string>();
  const [matchDateTo, setMatchDateTo] = useState<string>();

  // Bedroom and Garage options
  const BedroomOptions = ['1', '2', '3', '4', '5', '6'];
  const GarageOptions = [
    { label: 'None', value: '0' },
    { label: '1 Bay', value: '1' },
    { label: '2 Bay', value: '2' },
    { label: '3 Bay', value: '3' },
    { label: '4+ Bay', value: '4' },
  ];

  // Handler for selecting property types
  const onPropertyTypeSelect = (pt: PropertyTypes) => {
    if (filters.propertyType?.match(pt)) {
      // Removing the property type from the selected list if already selected
      setFilters({
        ...filters,
        propertyType: filter(
          (type) => type !== pt,
          [...(filters.propertyType || '').split(','), pt]
        ).join(','),
      });
    } else {
      // Adding the property type to the selected list if not already selected
      setFilters({
        ...filters,
        propertyType: uniq([
          ...(filters.propertyType || '').split(','),
          pt,
        ]).join(','),
      });
    }
  };

  // Handler for selecting bedroom options
  const onBedroomSelect = (value: string) => {
    const current = (filters.bedroom || '').split(',').filter(Boolean);
    const updated = current.includes(value)
      ? current.filter((v: string) => v !== value)
      : [...current, value];

    setFilters({ ...filters, bedroom: updated.join(',') });
  };

  // Handler for selecting garage options
  const onGarageSelect = (value: string) => {
    const current = (filters.garage || '').split(',').filter(Boolean);
    const updated = current.includes(value)
      ? current.filter((v: string) => v !== value)
      : [...current, value];

    setFilters({ ...filters, garage: updated.join(',') });
  };

  // Apply the filters and update the query string in the URL
  const onApply = () => {
    props.onClose(); // Close the modal
    history.push({
      pathname: location.pathname,
      search:
        '?' +
        new URLSearchParams(
          reject(isNil)(
            reject(isEmpty)({ ...filters, matchDateFrom, matchDateTo })
          ) as Record<string, string>
        ).toString(),
    });
  };

  // Reset the filters when the modal is closed
  useEffect(() => {
    if (!props.isOpen) {
      setFilters({});
      setMatchDateFrom(undefined);
      setMatchDateTo(undefined);
    } else {
      const searches = Object.fromEntries(new URLSearchParams(location.search));
      setFilters(searches);
      setMatchDateFrom(searches.matchDateFrom || undefined);
      setMatchDateTo(searches.matchDateTo || undefined);
    }
  }, [props.isOpen]);

  return (
    <AppModal
      {...props}
      size="xl"
      title="Filter"
      FooterComponent={
        <HStack spacing={5}>
          {/* Reset button to clear all filters */}
          <Button
            kind="secondary"
            onClick={() => {
              setFilters({});
              setMatchDateFrom('');
              setMatchDateTo('');
            }}
          >
            Reset
          </Button>
          {/* Apply button to submit the selected filters */}
          <Button onClick={onApply}>Apply</Button>
        </HStack>
      }
    >
      {/* Property Type Section */}
      <Typography variant="type8" weight="700" color="shade5">
        Property Type
      </Typography>
      <Flex my={2} flexWrap="wrap">
        {Object.values(PropertyTypes).map((propertyType) => (
          <Flex m={1} key={propertyType}>
            <Button
              onClick={() => onPropertyTypeSelect(propertyType)}
              outline={!filters.propertyType?.match(propertyType)}
              variant="tiny"
            >
              {ucFirstMultiple(propertyType)}
            </Button>
          </Flex>
        ))}
      </Flex>

      {/* Bedrooms Section */}
      <Typography variant="type8" weight="700" color="shade5">
        Bedrooms
      </Typography>
      <Flex my={2} flexWrap="wrap">
        {BedroomOptions.map((option) => (
          <Flex key={option} m={1}>
            <Button
              onClick={() => onBedroomSelect(option)}
              outline={!filters.bedroom?.split(',').includes(option)}
              variant="tiny"
              style={{ width: 50 }}
            >
              {option}
            </Button>
          </Flex>
        ))}
      </Flex>

      {/* Garage Section */}
      <Typography variant="type8" weight="700" color="shade5">
        Garage
      </Typography>
      <Flex my={2} flexWrap="wrap">
        {GarageOptions.map((option) => (
          <Flex key={option.value} m={1}>
            <Button
              style={{ width: 70 }}
              onClick={() => onGarageSelect(option.value)}
              outline={!filters.garage?.split(',').includes(option.value)}
              variant="tiny"
            >
              {option.label}
            </Button>
          </Flex>
        ))}
      </Flex>

      {/* Price Range Section */}
      <SimpleGrid columns={2} spacingX={5} spacingY={2}>
        <Box>
          <Typography variant="type8" weight="700" color="shade5">
            Price From
          </Typography>
          <Flex flexDir="column" my={1}>
            <Select
              options={marketPrice.buyer}
              onChange={(val) =>
                setFilters({ ...filters, priceFrom: val.target.value })
              }
              value={filters.priceFrom || ''}
              placeholder="Select Price"
            />
          </Flex>
        </Box>

        <Box>
          <Typography variant="type8" weight="700" color="shade5">
            Price To
          </Typography>
          <Flex flexDir="column" my={1}>
            <Select
              options={marketPrice.buyer}
              onChange={(val) =>
                setFilters({ ...filters, priceTo: val.target.value })
              }
              value={filters.priceTo || ''}
              placeholder="Select Price"
            />
          </Flex>
        </Box>
      </SimpleGrid>

      {/* Location and Date Filters */}
      <SimpleGrid columns={2} spacingX={5} spacingY={2}>
        <Box>
          <Typography variant="type8" weight="700" color="shade5">
            Location
          </Typography>
          <Flex flexDir="column" my={1}>
            <Select
              options={regions}
              onChange={(val) =>
                setFilters({ ...filters, region: val.target.value })
              }
              value={filters.region || ''}
              placeholder="Select Region"
            />
          </Flex>
        </Box>

        {/* Match Date From */}
        <Box>
          <Typography variant="type8" weight="700" color="shade5">
            Match Date from
          </Typography>
          <Flex flexDir="column" my={1}>
            <DatePicker
              value={matchDateFrom ? parseISO(matchDateFrom) : undefined}
              textFieldProps={{
                placeholder: 'MM/DD/YYYY',
              }}
              onChange={([date]) =>
                setMatchDateFrom(
                  set(date, {
                    seconds: 0,
                    milliseconds: 0,
                  }).toISOString()
                )
              }
            />
          </Flex>
        </Box>

        {/* Match Date To */}
        <Box>
          <Typography variant="type8" weight="700" color="shade5">
            Match Date to
          </Typography>
          <Flex flexDir="column" my={1}>
            <DatePicker
              value={matchDateTo ? parseISO(matchDateTo) : undefined}
              textFieldProps={{
                placeholder: 'MM/DD/YYYY',
              }}
              onChange={([date]) =>
                setMatchDateTo(
                  set(date, {
                    seconds: 0,
                    milliseconds: 0,
                  }).toISOString()
                )
              }
            />
          </Flex>
        </Box>
      </SimpleGrid>
    </AppModal>
  );
};

export default QuickListingFilters;
